/**
 * CarrierNumbersPage — Carrier-Recording Integration Sprint CR-D (2026-05-01)
 *
 * Admin UI for managing virtual-number-to-rep assignments.
 *
 * APIs consumed:
 *   GET  /admin/carrier-numbers                  — list assignments
 *   POST /admin/carrier-numbers                  — create assignment
 *   PUT  /admin/carrier-numbers/:id              — update status / userId / notes
 *   DEL  /admin/carrier-numbers/:id              — soft-delete
 *   GET  /admin/carrier-numbers/users-eligible   — reps eligible for virtual numbers
 *   GET  /admin/carrier-numbers/stats?days=30    — stats summary (CR-C, defensive)
 *   GET  /admin/carrier-numbers/stats/health     — per-vendor health (CR-C, defensive)
 *   GET  /admin/tenant-config                    — carrier recording enabled flag
 */

import { useEffect, useState, useCallback } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import type { ButtonHTMLAttributes } from 'react';
import {
  Phone, PhoneOff, Plus, X, CheckCircle2, AlertCircle,
  ChevronRight, Users, Clock, Mic,
} from 'lucide-react';
import { api } from '../../utils/api';
import { Modal, useModalClose } from '@/components/ui/Modal';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Assignment {
  id:               string;
  virtualNumber:    string;
  vendor:           string;
  vendorRefId?:     string;
  status:           'active' | 'paused' | 'deactivated';
  notes?:           string;
  assignedAt:       string;
  deactivatedAt?:   string;
  callsThisMonth:   number;
  minutesThisMonth: number;
  user: {
    id:         string;
    firstName?: string;
    lastName?:  string;
    email:      string;
  };
}

interface EligibleUser {
  userId:                 string;
  name:                   string;
  email:                  string;
  currentAssignmentCount: number;
}

interface StatsData {
  totalCallsThisMonth:   number;
  totalMinutesRecorded:  number;
  activeVirtualNumbers:  number;
}

interface VendorHealth {
  vendor:       string;
  lastWebhook?: string;
  healthy:      boolean;
}

const VENDOR_LABELS: Record<string, string> = {
  exotel:      'Exotel',
  myoperator:  'MyOperator',
  knowlarity:  'Knowlarity',
  tata_smartflo: 'Tata Smartflo',
  other:       'Other',
};

const VENDORS = Object.keys(VENDOR_LABELS);

// ─── Helpers ──────────────────────────────────────────────────────────────────

function initials(u: Assignment['user']): string {
  const fn = u.firstName ?? '';
  const ln = u.lastName ?? '';
  if (fn || ln) return `${fn[0] ?? ''}${ln[0] ?? ''}`.toUpperCase();
  return u.email[0]?.toUpperCase() ?? '?';
}

function displayName(u: Assignment['user']): string {
  const fn = u.firstName ?? '';
  const ln = u.lastName ?? '';
  const full = `${fn} ${ln}`.trim();
  return full || u.email;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  return `${Math.floor(hrs / 24)} days ago`;
}

// ─── Status pill ─────────────────────────────────────────────────────────────

function StatusPill({ status }: { status: string }) {
  const cfg: Record<string, { label: string; bg: string; color: string }> = {
    active:      { label: 'Active',      bg: '#D1FAE5', color: '#065F46' },
    paused:      { label: 'Paused',      bg: '#FEF3C7', color: '#92400E' },
    deactivated: { label: 'Deactivated', bg: '#F3F4F6', color: '#6B7280' },
  };
  const s = cfg[status] ?? cfg.deactivated;
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold"
      style={{ background: s.bg, color: s.color }}
    >
      {s.label}
    </span>
  );
}

// ─── Section card (matches AdminSettingsPage pattern) ────────────────────────

function SectionCard({
  icon: Icon,
  title,
  children,
}: {
  icon: React.ElementType;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className="rounded-xl border p-5"
      style={{ background: 'var(--ivory-0)', borderColor: 'var(--line)' }}
    >
      <div className="flex items-center gap-2 mb-4">
        <Icon size={17} style={{ color: 'var(--saffron)' }} />
        <h2 className="text-sm font-semibold" style={{ color: 'var(--ink-1)' }}>
          {title}
        </h2>
      </div>
      {children}
    </div>
  );
}

// ─── Stats card ───────────────────────────────────────────────────────────────

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div
      className="flex-1 rounded-lg border p-4 min-w-[140px]"
      style={{ background: 'var(--ivory-1, #F9F5EF)', borderColor: 'var(--line)' }}
    >
      <div className="text-2xl font-bold" style={{ color: 'var(--ink-0)', fontFamily: 'Fraunces, serif' }}>
        {value}
      </div>
      <div className="text-xs mt-1" style={{ color: 'var(--ink-3)' }}>{label}</div>
    </div>
  );
}

// ─── Add/Reassign Modal ───────────────────────────────────────────────────────

interface ModalProps {
  mode:        'create' | 'reassign';
  existing?:   Assignment;
  eligible:    EligibleUser[];
  onClose:     () => void;
  onSaved:     () => void;
}

function AssignmentModal({ mode, existing, eligible, onClose, onSaved }: ModalProps) {
  const [userId,      setUserId]      = useState(existing?.user.id ?? '');
  const [number,      setNumber]      = useState('');  // 10-digit suffix
  const [vendor,      setVendor]      = useState('exotel');
  const [vendorRefId, setVendorRefId] = useState('');
  const [notes,       setNotes]       = useState(existing?.notes ?? '');
  const [saving,      setSaving]      = useState(false);
  const [error,       setError]       = useState<string | null>(null);

  const handleSubmit = async () => {
    setError(null);
    try {
      setSaving(true);
      if (mode === 'create') {
        const virtualNumber = `+91${number}`;
        await api.post('/admin/carrier-numbers', {
          userId,
          virtualNumber,
          vendor,
          vendorRefId: vendorRefId || undefined,
          notes:       notes || undefined,
        });
      } else if (existing) {
        await api.put(`/admin/carrier-numbers/${existing.id}`, {
          userId: userId || undefined,
          notes:  notes || undefined,
        });
      }
      onSaved();
      onClose();
    } catch (e: any) {
      const msg = e?.response?.data?.error ?? e?.message ?? 'Save failed';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open onClose={onClose} className="!max-w-md !rounded-2xl !bg-[var(--ivory-0)]">
      <div className="p-6 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold" style={{ fontFamily: 'Fraunces, serif', color: 'var(--ink-0)' }}>
            {mode === 'create' ? 'Assign New Virtual Number' : 'Reassign Virtual Number'}
          </h3>
          <RequestCloseButton className="p-1 rounded-md hover:opacity-60">
            <X size={16} style={{ color: 'var(--ink-2)' }} />
          </RequestCloseButton>
        </div>

        {error && (
          <div className="flex items-center gap-2 rounded-lg px-3 py-2 text-xs" style={{ background: '#FEF2F2', color: '#B91C1C', border: '1px solid #FCA5A5' }}>
            <AlertCircle size={14} />
            <span>{error}</span>
          </div>
        )}

        {/* Rep dropdown */}
        <div>
          <label className="block text-xs font-medium mb-1" style={{ color: 'var(--ink-2)' }}>
            Assigned Rep *
          </label>
          <select
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            className="w-full rounded-lg border px-3 py-2 text-sm"
            style={{ borderColor: 'var(--line)', background: 'var(--ivory-0)', color: 'var(--ink-1)' }}
          >
            <option value="">Select a rep…</option>
            {eligible.map((u) => (
              <option key={u.userId} value={u.userId}>
                {u.name || u.email} — {u.email}
                {u.currentAssignmentCount > 0 ? ` (${u.currentAssignmentCount} active)` : ''}
              </option>
            ))}
          </select>
        </div>

        {/* Virtual number (create only) */}
        {mode === 'create' && (
          <>
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: 'var(--ink-2)' }}>
                Virtual Number *
              </label>
              <div className="flex items-center gap-0">
                <span
                  className="px-3 py-2 rounded-l-lg border border-r-0 text-sm font-mono select-none"
                  style={{ background: 'var(--ivory-2, #F3EFE8)', borderColor: 'var(--line)', color: 'var(--ink-2)' }}
                >
                  +91
                </span>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={10}
                  placeholder="10-digit number"
                  value={number}
                  onChange={(e) => setNumber(e.target.value.replace(/\D/g, ''))}
                  className="flex-1 rounded-r-lg border px-3 py-2 text-sm font-mono"
                  style={{ borderColor: 'var(--line)', background: 'var(--ivory-0)', color: 'var(--ink-1)' }}
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: 'var(--ink-2)' }}>
                Vendor *
              </label>
              <select
                value={vendor}
                onChange={(e) => setVendor(e.target.value)}
                className="w-full rounded-lg border px-3 py-2 text-sm"
                style={{ borderColor: 'var(--line)', background: 'var(--ivory-0)', color: 'var(--ink-1)' }}
              >
                {VENDORS.map((v) => (
                  <option key={v} value={v}>{VENDOR_LABELS[v]}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: 'var(--ink-2)' }}>
                Vendor Reference ID
                <span className="font-normal ml-1" style={{ color: 'var(--ink-3)' }}>(optional — from vendor dashboard)</span>
              </label>
              <input
                type="text"
                placeholder="e.g. EX-1234"
                value={vendorRefId}
                onChange={(e) => setVendorRefId(e.target.value)}
                className="w-full rounded-lg border px-3 py-2 text-sm"
                style={{ borderColor: 'var(--line)', background: 'var(--ivory-0)', color: 'var(--ink-1)' }}
              />
            </div>
          </>
        )}

        {/* Notes */}
        <div>
          <label className="block text-xs font-medium mb-1" style={{ color: 'var(--ink-2)' }}>
            Notes
            <span className="font-normal ml-1" style={{ color: 'var(--ink-3)' }}>(optional)</span>
          </label>
          <textarea
            rows={2}
            placeholder="e.g. Sales escalation line for north region"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="w-full rounded-lg border px-3 py-2 text-sm resize-none"
            style={{ borderColor: 'var(--line)', background: 'var(--ivory-0)', color: 'var(--ink-1)' }}
          />
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-2 pt-1">
          <RequestCloseButton
            className="px-4 py-2 rounded-lg text-sm"
            style={{ color: 'var(--ink-2)' }}
          >
            Cancel
          </RequestCloseButton>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving || !userId || (mode === 'create' && number.length !== 10)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white transition-opacity disabled:opacity-50"
            style={{ background: 'var(--saffron)' }}
          >
            {saving && <div className="w-3.5 h-3.5 rounded-full border-2 border-white border-t-transparent animate-spin" />}
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function RequestCloseButton({ children, ...props }: ButtonHTMLAttributes<HTMLButtonElement>) {
  const { requestClose } = useModalClose();
  return <button type="button" {...props} onClick={requestClose}>{children}</button>;
}

// ─── Deactivate confirm modal ─────────────────────────────────────────────────

function DeactivateModal({
  assignment,
  onClose,
  onConfirm,
}: {
  assignment: Assignment;
  onClose:    () => void;
  onConfirm:  () => void;
}) {
  const [loading, setLoading] = useState(false);
  const handle = async () => {
    setLoading(true);
    onConfirm();
  };

  return (
    <Modal open onClose={onClose} className="!max-w-sm !rounded-2xl !bg-[var(--ivory-0)]">
      <div className="p-6 space-y-4">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-full" style={{ background: '#FEE2E2' }}>
            <PhoneOff size={18} style={{ color: '#DC2626' }} />
          </div>
          <div>
            <h3 className="text-sm font-semibold" style={{ color: 'var(--ink-0)' }}>
              Deactivate virtual number?
            </h3>
            <p className="text-xs mt-1" style={{ color: 'var(--ink-3)' }}>
              <span className="font-mono">{assignment.virtualNumber}</span> assigned to{' '}
              {displayName(assignment.user)} will be deactivated. The record is retained for audit.
            </p>
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <RequestCloseButton
            className="px-4 py-2 rounded-lg text-sm"
            style={{ color: 'var(--ink-2)' }}
          >
            Cancel
          </RequestCloseButton>
          <button
            onClick={handle}
            disabled={loading}
            className="px-4 py-2 rounded-lg text-sm font-medium text-white"
            style={{ background: '#DC2626', opacity: loading ? 0.6 : 1 }}
          >
            Deactivate
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function CarrierNumbersPage() {
  const [assignments,   setAssignments]   = useState<Assignment[]>([]);
  const [eligible,      setEligible]      = useState<EligibleUser[]>([]);
  const [stats,         setStats]         = useState<StatsData | null>(null);
  const [vendorHealth,  setVendorHealth]  = useState<VendorHealth[] | null>(null);
  const [carrierEnabled, setCarrierEnabled] = useState<boolean | null>(null);

  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState<string | null>(null);

  const [showCreate,     setShowCreate]     = useState(false);
  const [reassigning,    setReassigning]    = useState<Assignment | null>(null);
  const [deactivating,   setDeactivating]   = useState<Assignment | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [listRes, eligibleRes, configRes] = await Promise.allSettled([
        api.get('/admin/carrier-numbers'),
        api.get('/admin/carrier-numbers/users-eligible'),
        api.get('/admin/tenant-config'),
      ]);

      if (listRes.status === 'fulfilled' && listRes.value.data?.data) {
        setAssignments(listRes.value.data.data);
      } else if (listRes.status === 'rejected') {
        setError('Failed to load assignments');
      }

      if (eligibleRes.status === 'fulfilled' && eligibleRes.value.data?.data) {
        setEligible(eligibleRes.value.data.data);
      }

      if (configRes.status === 'fulfilled' && configRes.value.data?.data) {
        const cfg = configRes.value.data.data;
        setCarrierEnabled(cfg.carrierRecordingEnabled ?? false);
      }

      // Stats — defensive: ignore 404/500
      try {
        const statsRes = await api.get('/admin/carrier-numbers/stats', { params: { days: 30 } });
        if (statsRes.data?.data) setStats(statsRes.data.data);
      } catch {
        // CR-C endpoint not yet deployed — render page without stats
      }

      // Vendor health — defensive: silently hide section if missing
      try {
        const healthRes = await api.get('/admin/carrier-numbers/stats/health');
        if (healthRes.data?.data) setVendorHealth(healthRes.data.data);
      } catch {
        // Not yet deployed — hide section
      }
    } catch (e: any) {
      setError(e?.response?.data?.error ?? e?.message ?? 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleToggleStatus = async (a: Assignment, newStatus: 'active' | 'paused') => {
    try {
      await api.put(`/admin/carrier-numbers/${a.id}`, { status: newStatus });
      load();
    } catch (e: any) {
      setError(e?.response?.data?.error ?? 'Status update failed');
    }
  };

  const handleDeactivateConfirm = async () => {
    if (!deactivating) return;
    try {
      await api.delete(`/admin/carrier-numbers/${deactivating.id}`);
      setDeactivating(null);
      load();
    } catch (e: any) {
      setError(e?.response?.data?.error ?? 'Deactivation failed');
      setDeactivating(null);
    }
  };

  // ── Render loading
  if (loading) {
    return (
      <div className="aura-root flex items-center justify-center py-24">
        <div className="w-6 h-6 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: 'var(--saffron)' }} />
      </div>
    );
  }

  const activeCount = assignments.filter((a) => a.status === 'active').length;

  return (
    <div className="aura-root max-w-5xl mx-auto px-4 pb-16 pt-6">

      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold" style={{ fontFamily: 'Fraunces, serif', color: 'var(--ink-0)' }}>
            Carrier Number Management
          </h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--ink-3)' }}>
            Virtual numbers issued by your call-recording vendor (Exotel / MyOperator / etc.). Each is mapped to a rep.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white ml-4 shrink-0"
          style={{ background: 'var(--saffron)' }}
        >
          <Plus size={15} />
          Add Virtual Number
        </button>
      </div>

      {/* Global error */}
      {error && (
        <div className="flex items-center gap-2 rounded-lg px-4 py-3 mb-4 text-sm" style={{ background: '#FEF2F2', color: '#B91C1C', border: '1px solid #FCA5A5' }}>
          <AlertCircle size={16} />
          <span>{error}</span>
          <button className="ml-auto" onClick={() => setError(null)}>
            <X size={14} />
          </button>
        </div>
      )}

      {/* Carrier recording status callout */}
      {carrierEnabled === false && (
        <div className="flex items-start gap-3 rounded-lg px-4 py-3 mb-4" style={{ background: '#FFFBEB', border: '1px solid #FCD34D' }}>
          <AlertCircle size={16} style={{ color: '#D97706', marginTop: 1 }} />
          <div className="text-sm" style={{ color: '#92400E' }}>
            <span className="font-semibold">Carrier recording is disabled</span> for this tenant.
            {' '}To activate, enable Carrier Recording in{' '}
            <a href="/admin/settings" className="underline">AURA Settings</a>
            {' '}and add active vendors.
          </div>
        </div>
      )}

      {carrierEnabled === true && (
        <div className="flex items-center gap-2 rounded-lg px-4 py-2.5 mb-4 text-sm" style={{ background: '#D1FAE5', border: '1px solid #6EE7B7' }}>
          <CheckCircle2 size={15} style={{ color: '#065F46' }} />
          <span style={{ color: '#065F46' }}>Carrier recording is <strong>enabled</strong> for this tenant.</span>
        </div>
      )}

      <div className="space-y-5">

        {/* Stats row */}
        <SectionCard icon={Mic} title="This Month's Activity">
          {stats ? (
            <div className="flex gap-3 flex-wrap">
              <StatCard label="Total calls this month" value={stats.totalCallsThisMonth ?? 0} />
              <StatCard label="Minutes recorded" value={stats.totalMinutesRecorded ?? 0} />
              <StatCard label="Active virtual numbers" value={stats.activeVirtualNumbers ?? activeCount} />
            </div>
          ) : (
            <div className="flex gap-3 flex-wrap">
              <StatCard label="Total calls this month" value="—" />
              <StatCard label="Minutes recorded" value="—" />
              <StatCard label="Active virtual numbers" value={activeCount} />
            </div>
          )}
          {!stats && (
            <p className="text-xs mt-3" style={{ color: 'var(--ink-3)' }}>
              Detailed stats will appear once the CR-C polling service is deployed.
            </p>
          )}
        </SectionCard>

        {/* Vendor health (hidden if CR-C's endpoint not up) */}
        {vendorHealth && vendorHealth.length > 0 && (
          <SectionCard icon={Phone} title="Vendor Health">
            <div className="flex gap-3 flex-wrap">
              {vendorHealth.map((v) => (
                <div
                  key={v.vendor}
                  className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm"
                  style={{
                    background: v.healthy ? '#D1FAE5' : '#FEF2F2',
                    border: `1px solid ${v.healthy ? '#6EE7B7' : '#FCA5A5'}`,
                    color:  v.healthy ? '#065F46' : '#B91C1C',
                  }}
                >
                  <span className="font-medium">{VENDOR_LABELS[v.vendor] ?? v.vendor}</span>
                  {v.lastWebhook && (
                    <span className="text-xs opacity-80">· last webhook {timeAgo(v.lastWebhook)}</span>
                  )}
                  <span>{v.healthy ? '✅ Healthy' : '⚠️ Issue'}</span>
                </div>
              ))}
            </div>
          </SectionCard>
        )}

        {/* Assignments table */}
        <SectionCard icon={Users} title="Virtual Number Assignments">
          {assignments.length === 0 ? (
            <div className="text-center py-10">
              <Phone size={32} style={{ color: 'var(--ink-3)', margin: '0 auto 12px' }} />
              <p className="text-sm font-medium" style={{ color: 'var(--ink-1)' }}>
                No virtual numbers assigned yet.
              </p>
              <p className="text-xs mt-1" style={{ color: 'var(--ink-3)' }}>
                Add one to start recording carrier-grade calls for compliance-critical reps.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto -mx-1">
              <table className="w-full text-sm" style={{ borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--line)' }}>
                    {['Virtual Number', 'Assigned To', 'Vendor', 'Status', 'Calls', 'Minutes', 'Assigned', 'Actions'].map((h) => (
                      <th
                        key={h}
                        className="text-left px-3 py-2 text-xs font-semibold"
                        style={{ color: 'var(--ink-3)', whiteSpace: 'nowrap' }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {assignments.map((a) => (
                    <tr
                      key={a.id}
                      style={{ borderBottom: '1px solid var(--line)' }}
                      className="hover:bg-black/[0.02] transition-colors"
                    >
                      {/* Virtual Number */}
                      <td className="px-3 py-3 font-mono text-xs" style={{ color: 'var(--ink-1)', whiteSpace: 'nowrap' }}>
                        {a.virtualNumber}
                      </td>

                      {/* Assigned to */}
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-2">
                          <div
                            className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 text-white"
                            style={{ background: 'var(--saffron)' }}
                          >
                            {initials(a.user)}
                          </div>
                          <div className="min-w-0">
                            <div className="text-xs font-medium truncate max-w-[140px]" style={{ color: 'var(--ink-1)' }}>
                              {displayName(a.user)}
                            </div>
                            <div className="text-xs truncate max-w-[140px]" style={{ color: 'var(--ink-3)' }}>
                              {a.user.email}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Vendor */}
                      <td className="px-3 py-3 text-xs" style={{ color: 'var(--ink-2)', whiteSpace: 'nowrap' }}>
                        {VENDOR_LABELS[a.vendor] ?? a.vendor}
                      </td>

                      {/* Status */}
                      <td className="px-3 py-3 whitespace-nowrap">
                        <StatusPill status={a.status} />
                      </td>

                      {/* Calls */}
                      <td className="px-3 py-3 text-xs text-center" style={{ color: 'var(--ink-2)' }}>
                        {a.callsThisMonth}
                      </td>

                      {/* Minutes */}
                      <td className="px-3 py-3 text-xs text-center" style={{ color: 'var(--ink-2)' }}>
                        {a.minutesThisMonth}
                      </td>

                      {/* Assigned date */}
                      <td className="px-3 py-3 text-xs whitespace-nowrap" style={{ color: 'var(--ink-3)' }}>
                        {formatDate(a.assignedAt)}
                      </td>

                      {/* Actions */}
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-1 whitespace-nowrap">
                          {a.status === 'active' && (
                            <button
                              onClick={() => handleToggleStatus(a, 'paused')}
                              className="px-2 py-1 rounded text-xs font-medium"
                              style={{ background: '#FEF3C7', color: '#92400E' }}
                              title="Pause"
                            >
                              Pause
                            </button>
                          )}
                          {a.status === 'paused' && (
                            <button
                              onClick={() => handleToggleStatus(a, 'active')}
                              className="px-2 py-1 rounded text-xs font-medium"
                              style={{ background: '#D1FAE5', color: '#065F46' }}
                              title="Resume"
                            >
                              Resume
                            </button>
                          )}
                          <button
                            onClick={() => setReassigning(a)}
                            className="px-2 py-1 rounded text-xs font-medium"
                            style={{ background: 'var(--ivory-2, #F3EFE8)', color: 'var(--ink-2)' }}
                            title="Reassign"
                          >
                            Reassign
                          </button>
                          {a.status !== 'deactivated' && (
                            <button
                              onClick={() => setDeactivating(a)}
                              className="px-2 py-1 rounded text-xs font-medium"
                              style={{ background: '#FEE2E2', color: '#DC2626' }}
                              title="Deactivate"
                            >
                              Deactivate
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </SectionCard>

      </div>

      {/* Modals */}
      {showCreate && (
        <AssignmentModal
          mode="create"
          eligible={eligible}
          onClose={() => setShowCreate(false)}
          onSaved={load}
        />
      )}

      {reassigning && (
        <AssignmentModal
          mode="reassign"
          existing={reassigning}
          eligible={eligible}
          onClose={() => setReassigning(null)}
          onSaved={load}
        />
      )}

      {deactivating && (
        <DeactivateModal
          assignment={deactivating}
          onClose={() => setDeactivating(null)}
          onConfirm={handleDeactivateConfirm}
        />
      )}
    </div>
  );
}
